// src/app/core/guards/entry-exists.guard.ts
import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  RedirectCommand,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

type EntryIndex = {
  module: string;
  slug: string;
};

function joinUrl(base: string, path: string): string {
  const b = base.replace(/\/+$/, '');
  const p = path.replace(/^\/+/, '');
  return `${b}/${p}`;
}

function normParam(v: string | null): string {
  return (v ?? '').toString().trim();
}

function isEntryIndex(x: unknown): x is EntryIndex {
  if (!x || typeof x !== 'object' || Array.isArray(x)) return false;
  const r = x as Record<string, unknown>;
  return typeof r['module'] === 'string' && typeof r['slug'] === 'string';
}

function redirect404(router: Router): RedirectCommand {
  // Replace the bad URL in history so Back returns to the previous page.
  return new RedirectCommand(router.parseUrl('/404'), { replaceUrl: true });
}

/**
 * Guard: validates (module, slug) exists in assets/v1/entries.json
 * BUT only when useStaticContent=true.
 */
export const entryExistsGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot,
): Observable<boolean | RedirectCommand> => {
  const router = inject(Router);
  const http = inject(HttpClient);

  // Local CSR / live API mode: don't block navigation here
  if (!environment.useStaticContent) {
    return of(true);
  }

  const module = normParam(route.paramMap.get('module'));
  const slug = normParam(route.paramMap.get('slug'));

  if (!module || !slug) {
    return of(redirect404(router));
  }

  const staticBase = (environment.staticBasePath ?? '/assets/v1').toString();
  const entriesUrl = joinUrl(staticBase, 'entries.json');

  return http.get<unknown>(entriesUrl).pipe(
    map((res) => (Array.isArray(res) ? res : [])),
    map((list) => {
      const exists = list.some((e) => {
        if (!isEntryIndex(e)) return false;
        return e.module.trim() === module && e.slug.trim() === slug;
      });

      return exists ? true : redirect404(router);
    }),
    // if entries.json is missing/broken in a static build, fail closed to /404
    catchError(() => of(redirect404(router))),
  );
};
